import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { or, gt } from 'ember-truth-helpers';
import { HdsLinkInline } from '@hashicorp/design-system-components/components';
import { CoreModalConfirm, Typography } from 'core/components';
import { boxMargin } from 'core/modifiers';

/**
 *
 * ## Overview
 * `ServicePrincipalDeleteModalComponent` renders Core::Modal::Confirm with or without a block of body.
 *  The body block renders only if a Service principal has one or more resources, in this case keys or
 *  workload identity providers.  This component requires a callback method so that users
 *  can delete a Service principal.
 *
 * @param {Function} onCancel - Callback method to reset servicePrincipal
 * @param {Function} onSubmit - Callback method to delete servicePrincipal
 * @param {Object} servicePrincipalToDelete - HCP service principal to delete
 * @param {Array} keys - an array of key resources that are attached to a service principal
 * @param {Array} wips - an array of workload identity provider resources that are attached to a service principal
 *
 * @example
 * <ServicePrincipal::DeleteModal
 *  @onCancel={{this.onCancel}}
 *  @onSubmit={{this.onSubmit}}
 *  @servicePrincipalToDelete={{this.servicePrincipal}}
 *  @keys={{this.keys}}
 *  @wips={{this.wips}}
 * />
 *
 * @class ServicePrincipalDeleteModalComponent
 *
 */
export default class ServicePrincipalDeleteModal extends Component {
  <template>
    <CoreModalConfirm
      @confirmText={{t "components.core.modal.confirm.delete"}}
      @headerIcon="alert-triangle"
      @headerText={{t
        "components.page.access-control.service-principals.delete-modal.with-resources.title"
        htmlSafe=true
        servicePrincipalName=@servicePrincipalToDelete.name
      }}
      @onCancel={{@onCancel}}
      @onSubmit={{@onSubmit}}
    >
      <:body>
        {{#let
          (or @keys.length @wips.length) (gt @keys.length 0) (gt @wips.length 0)
          as |hasResources hasKeys hasWips|
        }}
          <Typography
            @variant="body1"
            class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
            {{boxMargin "0 0 sm 0"}}
            data-test-service-principal-delete-modal-body-1
          >
            {{t
              "components.page.access-control.service-principals.delete-modal.with-resources.body-1"
              htmlSafe=true
              servicePrincipalName=@servicePrincipalToDelete.name
              spHasResources=hasResources
            }}

          </Typography>
          {{#if hasResources}}
            <Typography
              @component="p"
              class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
              {{boxMargin "0 0 sm 0"}}
              data-test-service-principal-delete-modal-resources-list
            >
              <div {{boxMargin "0 0 sm 0"}}>
                {{t
                  "components.page.access-control.service-principals.delete-modal.with-resources.list.title"
                }}
              </div>
              <ul {{boxMargin "0"}}>
                {{#if hasKeys}}
                  <li
                    data-test-service-principal-delete-modal-resources-item-keys
                  >
                    <HdsLinkInline
                      @route="cloud.access-control.service-principals.detail.keys"
                      @color="secondary"
                      @model={{@servicePrincipalToDelete.id}}
                      data-test-service-principal-delete-modal-resources-item-keys-list-link
                    >
                      {{t
                        "components.page.access-control.service-principals.delete-modal.with-resources.list.item.keys"
                        htmlSafe=true
                        keysCount=@keys.length
                      }}
                    </HdsLinkInline>
                  </li>
                {{/if}}
                {{#if hasWips}}
                  <li
                    data-test-service-principal-delete-modal-resources-item-wips
                  >
                    <HdsLinkInline
                      @route="cloud.access-control.service-principals.detail.workload-identity-providers"
                      @color="secondary"
                      @model={{@servicePrincipalToDelete.id}}
                      data-test-service-principal-delete-modal-resources-item-wips-list-link
                    >
                      {{t
                        "components.page.access-control.service-principals.delete-modal.with-resources.list.item.wips"
                        htmlSafe=true
                        wipsCount=@wips.length
                      }}
                    </HdsLinkInline>
                  </li>
                {{/if}}
              </ul>
            </Typography>
          {{/if}}
          <Typography
            @variant="body2"
            class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
            data-test-service-principal-delete-modal-body-2
            {{boxMargin "0 0 lg 0"}}
          >
            {{t
              "components.page.access-control.service-principals.delete-modal.with-resources.body-2"
            }}</Typography>
        {{/let}}
      </:body>
    </CoreModalConfirm>
  </template>
}
